import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { setAdminEmail } from '../lib/auth';
import BrandMark from '../components/brand/BrandMark';

export default function AdminLoginPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow, noarchive';
        document.head.appendChild(meta);
        const origTitle = document.title;
        document.title = 'Admin · DigiTech Cloud AI';
        return () => {
            try { document.head.removeChild(meta); } catch (_) { /* removed already */ }
            document.title = origTitle;
        };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            // Backend sets a httpOnly cookie; we only stash the (non-sensitive) email for UI display.
            const res = await api.post('/admin/login', { email, password });
            setAdminEmail(res.data?.email || email);
            toast.success('Welcome back');
            navigate('/admin/dashboard');
        } catch (err) {
            const msg = err?.response?.data?.detail || 'Login failed';
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="dt-space-bg min-h-screen flex items-center justify-center px-4" data-testid="admin-login">
            <div className="absolute inset-0 dt-grid-overlay dt-noise pointer-events-none" />
            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md relative"
            >
                <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
                    <BrandMark className="h-7 w-7" />
                    <span className="font-display text-lg font-semibold">DigiTech <span className="text-primary">Cloud AI</span></span>
                </Link>
                <div className="dt-neon-border">
                    <div className="dt-neon-inner rounded-[var(--radius)] p-8">
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-[11px] tracking-[0.22em] text-primary">ADMIN ACCESS</span>
                        </div>
                        <h1 className="font-display mt-2 text-2xl font-semibold">Sign in</h1>
                        <p className="text-sm text-muted-foreground mt-1">Restricted area — staff only.</p>
                        <form onSubmit={onSubmit} className="mt-6 space-y-4">
                            <div className="space-y-1.5">
                                <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">Email</Label>
                                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="admin-login-email-input" className="bg-white/5 border-white/10 focus-visible:ring-primary" placeholder="admin@digitechcloud-ai.com" />
                            </div>
                            <div className="space-y-1.5">
                                <Label className="text-xs font-mono uppercase tracking-widest text-foreground/70">Password</Label>
                                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="admin-login-password-input" className="bg-white/5 border-white/10 focus-visible:ring-primary" />
                            </div>
                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="admin-login-submit-button"
                                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_0_1px_rgba(0,229,255,0.35),0_0_24px_rgba(0,229,255,0.18)]"
                            >
                                {loading ? 'Signing in…' : (<><LogIn className="h-4 w-4 mr-1.5" /> Sign in</>)}
                            </Button>
                        </form>
                        <div className="mt-6 text-[11px] font-mono uppercase tracking-widest text-muted-foreground">
                            <span className="text-primary">DEMO</span> · admin@digitechcloud-ai.com / DigiTech@2026
                        </div>
                    </div>
                </div>
                <div className="mt-6 text-center text-xs text-muted-foreground">
                    <Link to="/" className="hover:text-primary">← Back to site</Link>
                </div>
            </motion.div>
        </div>
    );
}
